import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';

// Theme
import theme from './styles/theme';

// Auth Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import Sidebar from './components/layout/Sidebar';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Nominate from './pages/Nominate';
import Vote from './pages/Vote';
import AdminConsole from './pages/AdminConsole';

// Protected Route Component
const ProtectedRoute = ({ element, requiredRole }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</Box>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return element;
};

function AppContent() {
  // Get auth context
  const { user } = useAuth();
  
  return (
    <Box sx={{
      display: 'flex',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)', // Greyish gradient for better contrast
    }}>
      {user && <Sidebar />}
      
      <Box component="main" sx={{ 
        flexGrow: 1,
        p: 3,
        ml: user ? { xs: 0, sm: '64px' } : 0
      }}>
        <Routes>
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" replace />} />
          <Route path="/register" element={!user ? <Register /> : <Navigate to="/dashboard" replace />} />
          <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
          <Route path="/nominate" element={<ProtectedRoute element={<Nominate />} />} />
          <Route path="/vote" element={<ProtectedRoute element={<Vote />} />} />
          <Route path="/admin" element={<ProtectedRoute element={<AdminConsole />} requiredRole="admin" />} />
          <Route path="/" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />
        </Routes>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
